import { type BaseEntity } from './base-entity';
import { JSON_API_RELATIONSHIP_DECORATOR_KEY } from './decorators/json-api-relationship.decorator';
import { JSON_API_RESOURCE_DECORATOR_KEY } from './decorators/json-api-resource.decorator';
import { type EntityManager } from './entity-manager';

type RefType = { type: string; relationship?: string } & ({ id: string } | { lid: string });

const randomUUID = (): string => {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};

export class AtomicOperation<T extends BaseEntity> {
  op: 'add' | 'remove' | 'update';
  data?: T | { type: string; id: BaseEntity['id'] } | Array<{ type: string; id: BaseEntity['id'] }>;
  lid?: T['id'];
  href?: string;
  ref?: RefType;
  meta?: Record<string, any>;

  private constructor(
    op: AtomicOperation<T>['op'],
    data: AtomicOperation<T>['data'],
    lid?: AtomicOperation<T>['lid'],
    href?: AtomicOperation<T>['href'],
    ref?: AtomicOperation<T>['ref'],
    meta?: AtomicOperation<T>['meta']
  ) {
    this.op = op;
    this.data = data;
    this.lid = lid;
    this.href = href;
    this.ref = ref;
    this.meta = meta;
  }

  public static add<P extends BaseEntity>(
    data:
      | P
      | { type: string; id: BaseEntity['id'] }
      | Array<{ type: string; id: BaseEntity['id'] }>,
    lid?: P['id'],
    href?: AtomicOperation<P>['href'] | string[],
    ref?: AtomicOperation<P>['ref'],
    meta?: AtomicOperation<P>['meta']
  ): AtomicOperation<P> {
    return new AtomicOperation<P>(
      'add',
      data,
      lid,
      Array.isArray(href) ? href.toSpliced(0, 0, '').join('/') : href,
      !href ? ref : undefined,
      meta
    );
  }

  public static update<P extends BaseEntity>(
    data: P,
    lid?: P['id'],
    href?: AtomicOperation<P>['href'] | string[],
    ref?: AtomicOperation<P>['ref'],
    meta?: AtomicOperation<P>['meta']
  ): AtomicOperation<P> {
    return new AtomicOperation<P>(
      'update',
      data,
      lid,
      Array.isArray(href) ? href.toSpliced(0, 0, '').join('/') : href,
      !href ? ref : undefined,
      meta
    );
  }

  public static remove<P extends BaseEntity>(
    href?: AtomicOperation<P>['href'] | string[],
    ref?: AtomicOperation<P>['ref'],
    data?:
      | { type: string; id: BaseEntity['id'] }
      | Array<{ type: string; id: BaseEntity['id'] }>
      | undefined
  ): AtomicOperation<P> {
    return new AtomicOperation<P>(
      'remove',
      data,
      undefined,
      Array.isArray(href) ? href.toSpliced(0, 0, '').join('/') : href,
      !href ? ref : undefined,
      undefined
    );
  }

  public static addResource<P extends BaseEntity>(
    data: P,
    lid?: P['id'],
    meta?: AtomicOperation<P>['meta']
  ): AtomicOperation<P> {
    return new AtomicOperation<P>('add', data, lid, undefined, undefined, meta);
  }

  public static updateResource<P extends BaseEntity>(
    data: P,
    meta?: AtomicOperation<P>['meta']
  ): AtomicOperation<P> {
    return new AtomicOperation<P>('update', data, undefined, undefined, undefined, meta);
  }

  public static removeResource<P extends BaseEntity>(data: P): AtomicOperation<P> {
    return new AtomicOperation<P>('remove', data);
  }

  public static upsertResource<P extends BaseEntity>(
    data: P,
    lid?: string,
    forceUpdate: boolean = false,
    meta?: AtomicOperation<P>['meta']
  ): AtomicOperation<P> {
    if (forceUpdate || data.id) {
      return AtomicOperation.updateResource(data, meta);
    }
    return AtomicOperation.addResource(data, lid, meta);
  }

  public static removeNestedResource<T extends BaseEntity, P extends BaseEntity>(
    parentResource: T,
    data: P
  ): AtomicOperation<P> {
    return new AtomicOperation<P>(
      'remove',
      undefined,
      undefined,
      [
        '',
        Reflect.getMetadata(JSON_API_RESOURCE_DECORATOR_KEY, parentResource.constructor).type,
        parentResource.id!,
        Object.entries(
          Reflect.getMetadata(JSON_API_RELATIONSHIP_DECORATOR_KEY, parentResource.constructor)
        ).find(
          ([_, value]) =>
            value === Reflect.getMetadata(JSON_API_RESOURCE_DECORATOR_KEY, data.constructor).type
        )?.[0],
        data.id!,
      ].join('/')
    );
  }

  public static removeNestedResources<T extends BaseEntity, P extends BaseEntity>(
    parentResource: T,
    data: P[]
  ): Array<AtomicOperation<P>> {
    return data.map((item) => AtomicOperation.removeNestedResource(parentResource, item));
  }

  public static updateNestedResource<T extends BaseEntity, P extends BaseEntity>(
    parentResource: T,
    data: P
  ): AtomicOperation<P> {
    return new AtomicOperation<P>(
      'update',
      data,
      undefined,
      [
        '',
        Reflect.getMetadata(JSON_API_RESOURCE_DECORATOR_KEY, parentResource.constructor).type,
        parentResource.id!,
        Object.entries(
          Reflect.getMetadata(JSON_API_RELATIONSHIP_DECORATOR_KEY, parentResource.constructor)
        ).find(
          ([_, value]) =>
            value === Reflect.getMetadata(JSON_API_RESOURCE_DECORATOR_KEY, data.constructor).type
        )?.[0],
        data.id!,
      ].join('/')
    );
  }

  public static updateNestedResources<T extends BaseEntity, P extends BaseEntity>(
    parentResource: T,
    data: P[]
  ): Array<AtomicOperation<P>> {
    return data.map((item) => AtomicOperation.updateNestedResource(parentResource, item));
  }

  public static addNestedResource<T extends BaseEntity, P extends BaseEntity>(
    parentResource: T,
    parentResourceLid: T['id'] | undefined,
    data: P,
    dataLid?: P['id'] | undefined
  ): AtomicOperation<P> {
    return new AtomicOperation<P>(
      'add',
      data,
      dataLid,
      [
        '',
        Reflect.getMetadata(JSON_API_RESOURCE_DECORATOR_KEY, parentResource.constructor).type,
        parentResource.id ?? parentResourceLid ?? randomUUID(),
        Object.entries(
          Reflect.getMetadata(JSON_API_RELATIONSHIP_DECORATOR_KEY, parentResource.constructor)
        ).find(
          ([_, value]) =>
            value === Reflect.getMetadata(JSON_API_RESOURCE_DECORATOR_KEY, data.constructor).type
        )?.[0],
        data.id!,
      ].join('/')
    );
  }

  public static addNestedResources<T extends BaseEntity, P extends BaseEntity>(
    parentResource: T,
    parentResourceLid: T['id'] | undefined,
    data: P[],
    dataLids?: Array<P['id']>
  ): Array<AtomicOperation<P>> {
    return data.map((item, idx) =>
      AtomicOperation.addNestedResource(parentResource, parentResourceLid, item, dataLids?.[idx])
    );
  }

  public serialize(entityManager: EntityManager): Omit<
    Omit<AtomicOperation<T>, 'serialize'>,
    'data'
  > & {
    data: Record<string, any> | null | undefined;
  } {
    return {
      op: this.op,
      href: this.href,
      ref: this.ref,
      meta: this.meta,
      data:
        this.ref?.relationship || Array.isArray(this.data)
          ? this.data
          : this.data
          ? Object.assign(
              entityManager.serialize(this.data) ?? {},
              this.data?.id
                ? {}
                : {
                    lid: this?.lid ?? randomUUID(),
                  }
            )
          : undefined,
    };
  }
}
